import axios from 'axios';

// API Configuration
const API_CONFIG = {
  baseURL: process.env.REACT_APP_BACKEND_URL || 'http://localhost:5000',
  timeout: 15000, // 15 seconds
  headers: {
    'Content-Type': 'application/json',
  },
};

// Create axios instance
const apiClient = axios.create(API_CONFIG);

// Request interceptor
apiClient.interceptors.request.use(
  (config) => {
    // Add auth token if available
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log('[API] Request with token:', { 
        url: config.url, 
        method: config.method,
        hasAuth: true,
        tokenPreview: token.substring(0, 20) + '...'
      });
    } else {
      console.warn('[API] Request without token:', {
        url: config.url,
        method: config.method,
        hasAuth: false
      });
    }
    
    // Log request in development
    if (process.env.REACT_APP_ENV === 'development') {
      console.log('API Request:', {
        method: config.method?.toUpperCase(),
        url: config.url,
        data: config.data,
      });
    }
    
    return config;
  },
  (error) => {
    console.error('Request Error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor
apiClient.interceptors.response.use(
  (response) => {
    // Log response in development
    if (process.env.REACT_APP_ENV === 'development') {
      console.log('API Response:', {
        status: response.status,
        url: response.config.url,
        data: response.data,
      });
    }
    
    return response;
  },
  (error) => {
    // Handle common errors
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;
      
      switch (status) {
        case 401:
          // Debug: Log the 401 error details
          const token = localStorage.getItem('token');
          console.error('[API] 401 Unauthorized Error:', {
            url: error.config?.url,
            hasToken: !!token,
            tokenPreview: token ? token.substring(0, 20) + '...' : 'NO_TOKEN',
            errorMessage: data?.message,
            errorData: data
          });
          
          // Only clear auth if token exists AND it's actually expired/invalid
          // Check if the error message indicates token expiration
          const isTokenExpired = data?.message?.toLowerCase().includes('expired') ||
                                data?.message?.toLowerCase().includes('invalid') ||
                                data?.message?.toLowerCase().includes('malformed');
          
          if (token && isTokenExpired) {
            // Token exists and is expired/invalid - clear it
            localStorage.removeItem('token');
            localStorage.removeItem('user');
            console.error('[API] Token expired/invalid - cleared from storage');
          } else if (token && !isTokenExpired) {
            // Token exists but 401 is for other reasons (missing header, wrong format, etc)
            console.warn('[API] 401 but token exists - may be backend validation issue, NOT clearing token');
          } else {
            console.warn('[API] 401 with no token stored');
          }
          break;
        case 403:
          console.error('Access forbidden');
          break;
        case 404:
          console.error('Resource not found');
          break;
        case 500:
          console.error('Server error - please try again later');
          break;
        default:
          console.error('API Error:', data?.message || 'Unknown error');
      }
    } else if (error.request) {
      // Request made but no response
      console.error('Network error - please check your connection');
    } else {
      // Something else happened
      console.error('Error:', error.message);
    }
    
    return Promise.reject(error);
  }
);

// API endpoints
export const API_ENDPOINTS = {
  // Services
  services: '/api/services',
  serviceById: (id) => `/api/services/${id}`,
  
  // Products
  products: '/api/products',
  productById: (id) => `/api/products/${id}`,
  
  // Cart - Support multiple endpoint patterns
  cart: '/api/cart',
  cartAdd: '/api/cart/add',           // POST /api/cart/add
  cartAddItem: '/api/cart',            // POST /api/cart with item data
  cartUpdate: '/api/cart/update',
  cartRemove: '/api/cart/remove',
  
  // Checkout
  checkout: '/api/create-order',
  orders: '/api/orders',
  orderById: (id) => `/api/orders/${id}`,
  
  // Contact
  contact: '/api/contact',
  
  // Auth (if needed)
  login: '/api/auth/login',
  register: '/api/auth/register',
  logout: '/api/auth/logout',
  profile: '/api/auth/profile',
  forgotPassword: '/api/auth/forgot-password',
  resetPassword: '/api/auth/reset-password',
  
  // Gallery (Admin)
  gallery: '/api/gallery',
  galleryById: (id) => `/api/gallery/${id}`,
  galleryWithImage: '/api/gallery/with-image',
  
  // Upload
  upload: '/api/upload',
};

// Currency configuration
const CURRENCY = 'INR'; // Default currency
const SUPPORTED_CURRENCIES = ['INR', 'USD', 'EUR']; // Add more currencies as needed

export { CURRENCY, SUPPORTED_CURRENCIES };

export default apiClient;
